import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Input from '../../../../../components/common-components/input/Input';
import Button from '../../../../../components/common-components/button/Button';
import { BUTTON_VARIANTS } from '../../../../../components/common-components/button/Constants';
import Icons from '../../../../../components/icons/Icons';
import Heading from '../../../../../components/common-components/zoom/components/heading/Heading';
import { HEADING } from '../../../../../components/common-components/zoom/components/heading/constants/constants';
import ItemManager from '../ItemManager';
import AddTelecomOrAddress from './AddTelecomOrAddress';
import Label from '../../../../../components/common-components/label/Label';
import HealthRecordHelper from '../utils/utils';
import SelectDropdown from '../../../../../components/common-components/selectDropdown';
import STATE_DISTRICTS from '../../../../../services/utils/StateDistrictsConstant.json';
import { getValidationSchema } from '../../../../../libs/formsUtils';
import TextArea from '../../../../../components/common-components/textArea/TextArea';
import { MANUFACTURER_FORM_FIELDS_NAMES } from '../Constants';

const fields = [
	{ fieldName: MANUFACTURER_FORM_FIELDS_NAMES.NAME, isRequired: true },
];

const validationSchema = getValidationSchema(fields);

const Manufacturer = ({ onConfirm, close }) => {
	const allStates = Object.keys(STATE_DISTRICTS).map((state) => ({
		label: state,
		value: state,
	}));

	const allDistricts = Object.values(STATE_DISTRICTS).flat();
	const districtOptions = allDistricts.map((dist) => ({
		label: dist,
		value: dist,
	}));

	const handleStateChange = (selectedDist, setFieldValue) => {
		const foundState = Object.keys(STATE_DISTRICTS).find((state) =>
			STATE_DISTRICTS[state].includes(selectedDist.value)
		);
		if (foundState) {
			const selected = { label: foundState, value: foundState };
			setFieldValue(MANUFACTURER_FORM_FIELDS_NAMES.STATE, selected, false);
		}
	};

	return (
		<Formik
			initialValues={{
				[MANUFACTURER_FORM_FIELDS_NAMES.NAME]: '',
				[MANUFACTURER_FORM_FIELDS_NAMES.ADDRESS_TEXT]:'',
				[MANUFACTURER_FORM_FIELDS_NAMES.EMAIL]:'',
				[MANUFACTURER_FORM_FIELDS_NAMES.STATE]: null,
				[MANUFACTURER_FORM_FIELDS_NAMES.CITY]: null,
				[MANUFACTURER_FORM_FIELDS_NAMES.POSTALCODE]: '',
			}}
			validationSchema={validationSchema}
			onSubmit={(values) => {
				const enrichedAddressList ={
					text:values[MANUFACTURER_FORM_FIELDS_NAMES.ADDRESS_TEXT],
					state: values[MANUFACTURER_FORM_FIELDS_NAMES.STATE]?.value,
					city: values[MANUFACTURER_FORM_FIELDS_NAMES.CITY],
					postalCode: values[MANUFACTURER_FORM_FIELDS_NAMES.POSTALCODE],
				};

				const data = {
					name: values[MANUFACTURER_FORM_FIELDS_NAMES.NAME],
					telecom:HealthRecordHelper.removeEmptyFields({email:values[MANUFACTURER_FORM_FIELDS_NAMES.EMAIL]}),
					address: HealthRecordHelper.removeEmptyFields(enrichedAddressList),
				};
				
				const cleanedData = HealthRecordHelper.removeEmptyFields(data);
				onConfirm((prev) => [...prev, { type: cleanedData.name, data: cleanedData }]);
				close();
			}}
		>
			{({ values, errors, touched, setFieldValue, handleBlur, handleSubmit }) => (
				<>
					<div className="fixed h-full inset-0 z-50 flex justify-center items-center bg-gray-800 bg-opacity-50">
						<div className="flex flex-col gap-4 bg-white shadow-lg rounded-lg p-6 w-[90%] max-w-5xl min-h-[35%] max-h-[90%] overflow-y-auto">
							<div className="flex justify-between items-center border-b pb-3">
								<Heading type={HEADING.H2} className="text-lg font-semibold">
									Add Manufacturer
								</Heading>
								<div className="hover:cursor-pointer" onClick={close}>
									<Icons iconName={'closeIcon'} />
								</div>
							</div>

							<form onSubmit={handleSubmit} className="flex flex-col gap-4">
								<div className="grid grid-cols-2 gap-2">
									<Input
										label="Manufacturer Name"
										isRequired
										placeholder="Enter manufacturer name"
										name={MANUFACTURER_FORM_FIELDS_NAMES.NAME}
										value={values[MANUFACTURER_FORM_FIELDS_NAMES.NAME]}
										onChangeCb={(e) => setFieldValue(MANUFACTURER_FORM_FIELDS_NAMES.NAME, e.target.value)}
										onBlurCb={handleBlur}
										error={touched[MANUFACTURER_FORM_FIELDS_NAMES.NAME] && errors[MANUFACTURER_FORM_FIELDS_NAMES.NAME]}
									/>
									<Input
										label="Email"
										placeholder="Enter  email"
										name={MANUFACTURER_FORM_FIELDS_NAMES.EMAIL}
										value={values[MANUFACTURER_FORM_FIELDS_NAMES.EMAIL]}
										onChangeCb={(e) => setFieldValue(MANUFACTURER_FORM_FIELDS_NAMES.EMAIL, e.target.value)}
										onBlurCb={handleBlur}
										error={touched[MANUFACTURER_FORM_FIELDS_NAMES.EMAIL] && errors[MANUFACTURER_FORM_FIELDS_NAMES.EMAIL]}
									/>
								</div>
								<div className="grid gap-2">
									<TextArea
										label={"Address"}
										name={MANUFACTURER_FORM_FIELDS_NAMES.ADDRESS_TEXT}
										value={values[MANUFACTURER_FORM_FIELDS_NAMES.ADDRESS_TEXT]}
										onChangeCb={(e) => {
											setFieldValue(MANUFACTURER_FORM_FIELDS_NAMES.ADDRESS_TEXT, e.target.value)
										}}
									/>
								</div>
								<div className="grid grid-cols-3 gap-2">
									<SelectDropdown
										label="City"
										placeholder="Select City"
										name={MANUFACTURER_FORM_FIELDS_NAMES.CITY}
										value={
											districtOptions.find((d) => d.value === values[MANUFACTURER_FORM_FIELDS_NAMES.CITY]) ?? {
												label: '',
												value: '',
											}
										}
										onChangeCb={(val) => {
											setFieldValue(MANUFACTURER_FORM_FIELDS_NAMES.CITY, val.value, true);
											handleStateChange(val, setFieldValue);
										}}
										onBlurCb={handleBlur}
										options={districtOptions}
										error={touched[MANUFACTURER_FORM_FIELDS_NAMES.CITY] && errors[MANUFACTURER_FORM_FIELDS_NAMES.CITY]}
									/>

									<SelectDropdown
										label="State"
										placeholder="Auto-filled from city"
										name={MANUFACTURER_FORM_FIELDS_NAMES.STATE}
										value={values[MANUFACTURER_FORM_FIELDS_NAMES.STATE] ?? { label: '', value: '' }}
										onChangeCb={() => {}}
										onBlurCb={handleBlur}
										options={allStates}
										disabled
									/>

									<Input
										label="Pin Code"
										placeholder="Enter Pin Code"
										name={MANUFACTURER_FORM_FIELDS_NAMES.POSTALCODE}
										value={values[MANUFACTURER_FORM_FIELDS_NAMES.POSTALCODE]}
										onChangeCb={(e) => setFieldValue(MANUFACTURER_FORM_FIELDS_NAMES.POSTALCODE, e.target.value)}
										onBlurCb={handleBlur}
										error={
											touched[MANUFACTURER_FORM_FIELDS_NAMES.POSTALCODE] &&
											errors[MANUFACTURER_FORM_FIELDS_NAMES.POSTALCODE]
										}
									/>
								</div>

								<div className="flex justify-end">
									<Button
										type="submit"
										variant={BUTTON_VARIANTS.CONTAINED}
										customBtnClass="px-6 py-2"
									>
										Submit
									</Button>
								</div>
							</form>
						</div>
					</div>
				</>
			)}
		</Formik>
	);
};

export default Manufacturer;
